/**
 * De schil van werkplek v6: menubalk links (op de iPhone onderin) en
 * het werkvlak. Eén live-verbinding voor het hele scherm.
 *
 * De menubalk klapt uit bij een klik op de balk en in bij een klik in het
 * werkvlak (ontwerp 26); dat regelt de menubalk zelf met een luisteraar
 * op het document.
 */
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";

import { IdleGuard } from "@/components/auth/IdleGuard";
import { PortaalMeldingen } from "@/components/layout/PortaalMeldingen";

import { LiveProvider, useNaHerverbinding } from "./live";
import { Menubalk, MobieleBalk } from "./Menubalk";
import { bijtrekken, useMenutellers, useVerversBijTerugkomst } from "./vangnet";
import "./v6.css";

/**
 * Na een herverbinding van de live-stroom één keer lijst, zaak en tellers
 * (er kan een event gemist zijn). Samen met de terugkomst op het scherm
 * (`useVerversBijTerugkomst`) geeft dat één herlading, niet twee.
 */
function useBijtrekken() {
  const qc = useQueryClient();
  useNaHerverbinding(() => bijtrekken(qc, ["lijst", "zaken"]));
  useVerversBijTerugkomst();
}

function isInvoer(el: Element | null): boolean {
  if (!el) return false;
  if (el instanceof HTMLTextAreaElement) return true;
  if (el instanceof HTMLInputElement) return !["checkbox", "radio", "button", "submit"].includes(el.type);
  return el instanceof HTMLElement && el.isContentEditable;
}

/**
 * Sessie 70 (Simulator, toetsenbord open): op de iPhone nemen de balk
 * onderin, de voet en het toetsenbord samen bijna het hele scherm. Zolang
 * JP typt, is de balk onderin weg; na het typen komt hij terug.
 */
function useTypt(): boolean {
  const [typt, setTypt] = useState(false);
  useEffect(() => {
    const kijk = () => setTypt(isInvoer(document.activeElement));
    // focusout vuurt vóór de nieuwe focus; kijk een tel later.
    const uit = () => window.setTimeout(kijk, 0);
    document.addEventListener("focusin", kijk);
    document.addEventListener("focusout", uit);
    return () => {
      document.removeEventListener("focusin", kijk);
      document.removeEventListener("focusout", uit);
    };
  }, []);
  return typt;
}

export function WerkplekV6Shell() {
  return (
    <LiveProvider>
      <ShellInhoud />
    </LiveProvider>
  );
}

/** Binnen de live-provider: tellers en bijtrekken weten of de stroom open is. */
function ShellInhoud() {
  const tellers = useMenutellers();
  useBijtrekken();
  const typt = useTypt();
  const aantallen = {
    vandaag: tellers.data?.vandaag ?? 0,
    retouren: tellers.data?.retouren ?? 0,
  };
  return (
    <div className={typt ? "v6 typt" : "v6"}>
      <Menubalk tellers={aantallen} />
      <main className="v6-werk">
        <Outlet />
      </main>
      <MobieleBalk tellers={aantallen} />
      {/* Toon en tabtitel bij een nieuwe chat, overdracht of mail. */}
      <PortaalMeldingen />
      {/* Sessie 77: uitloggen na 30 minuten stilte, weer gemonteerd na
          #698. Toets en een vertrouwd apparaat slaan hem over. */}
      <IdleGuard />
    </div>
  );
}
